import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import clsx from 'clsx'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import InputChip from 'LuxKit/Chips/InputChip'
import AssistChip from 'LuxKit/Chips/AssistChip'
import BodyText from 'LuxKit/Typography/BodyText'
import LineSpinnerIcon from 'LuxKit/Icons/line/LineSpinnerIcon'
import LinePlusCircleIcon from 'LuxKit/Icons/line/LinePlusCircleIcon'
import LineFileIcon from 'LuxKit/Icons/line/LineFileIcon'
import LinePaperclipIcon from 'LuxKit/Icons/line/LinePaperclipIcon'
import LineCalendarPlusIcon from 'LuxKit/Icons/line/LineCalendarPlusIcon'
import LineSquareShapeIcon from 'LuxKit/Icons/line/LineSquareShapeIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import SolidMicrophoneIcon from 'LuxKit/Icons/solid/SolidMicrophoneIcon'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import noop from 'lib/function/noop'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import useResizeObserver from 'hooks/useResizeObserver'
import { normaliseMessage } from 'tripPlanner/api/chatUtils'
import { trackVoiceInputUsed } from 'tripPlanner/utils/chatAnalytics'
import { useMicrophoneTranscriber } from './hooks/useMicrophoneTranscriber'
import RecordingOverlay from './RecordingOverlay'
import TextArea from 'components/Common/Form/Input/TextArea'
import ImageThumbnailPreview from './ImageThumbnailPreview'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import ChatInputShell from './ChatInputShell'
import MorphingSendButton from './MorphingSendButton'
import {
  ACCEPT_EXTENSIONS,
  getClipboardChatFiles,
  partitionChatFiles,
} from './chatFiles'

interface ChatMessageInputProps {
  onSubmit: (message: string) => void
  disabled?: boolean
  placeholder?: string
  value?: string
  onChange?: (value: string) => void
  disableSubmit?: boolean
  isStreaming?: boolean
  showStopButton?: boolean
  onStopStream?: () => void
  files?: Array<File>
  onFilesChange?: (files: Array<File>) => void
  selectedTags?: Array<string>
  onAddBookingClick?: () => void
  showAddBookingAction?: boolean
  onRemoveTag?: (tag: string) => void
  inputRef?: React.RefObject<HTMLTextAreaElement | null>
  hideDisclaimer?: boolean
  compactDisclaimer?: boolean
  disclaimerText?: string
  autoFocus?: boolean
  /** Blur the textarea after submit so mobile soft keyboards dismiss. */
  dismissKeyboardOnSubmit?: boolean
  /** Optional chatId for analytics tracking */
  chatId?: number
}

const MAX_HEIGHT = 160
const ROWS = 1
const COMPOSER_LINE_HEIGHT = 20
const MOBILE_FOCUSED_MIN_HEIGHT = COMPOSER_LINE_HEIGHT * 2
const MOBILE_COMPOSER_EXPAND_DURATION = '0.2s'
const MOBILE_COMPOSER_EXPAND_EASING = 'ease'
const MOBILE_VISIBLE_TAG_LIMIT = 4

const SelectedTagsGroup = styled(Group)`
  width: 100%;
  min-width: 0;
`

const ComposerInputChip = styled(InputChip)`
  min-width: 0;
  max-width: 100%;

  > *:not(svg):not(img) {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(8)};
  width: 100%;
`

const ComposerShellAnchor = styled.div`
  width: 100%;
`

const FilePreviewItem = styled.div`
  position: relative;
`

const RemoveFileButton = styled.button`
  position: absolute;
  top: ${rem(-6)};
  inset-inline-end: ${rem(-6)};
  width: ${rem(20)};
  height: ${rem(20)};
  border-radius: 50%;
  border: none;
  background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
  color: ${props => props.theme.palette.neutral.surface.darkContrast};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  z-index: 1;

  &:hover {
    background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
  }
`

const FileCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  padding: ${rem(12)} ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  border-radius: ${rem(8)};
  min-width: ${rem(160)};
  max-width: ${rem(240)};
`

const FileIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(40)};
  height: ${rem(40)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  border-radius: ${rem(8)};
  flex-shrink: 0;
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
`

const FileInfoWrapper = styled.div`
  min-width: 0;
  flex: 1;
`

// Hidden file picker — no typed text, so Input dir handling does not apply.
const HiddenFileInput = styled.input`
  display: none;
`

const TextAreaWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  width: 100%;

  &.stacked {
    align-items: flex-start;
  }

  label,
  span {
    width: 100%;
    min-width: 0;
    border: none;
  }

  textarea {
    height: ${rem(COMPOSER_LINE_HEIGHT)};
    min-height: ${rem(COMPOSER_LINE_HEIGHT)};
    padding: 0;
    line-height: ${rem(COMPOSER_LINE_HEIGHT)};
    transition: min-height ${MOBILE_COMPOSER_EXPAND_DURATION} ${MOBILE_COMPOSER_EXPAND_EASING};
  }

  &.hidePlaceholderOnFocus:not(.stacked) textarea:placeholder-shown {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }

  &.hidePlaceholderOnFocus textarea:focus::placeholder {
    color: transparent;
  }

  @media (prefers-reduced-motion: reduce) {
    textarea {
      transition: none;
    }
  }
`

const MobileComposer = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  grid-template-areas: 'attach textarea mic send';
  align-items: center;
  column-gap: ${rem(8)};
  row-gap: 0;
  width: 100%;
  transition: row-gap ${MOBILE_COMPOSER_EXPAND_DURATION} ${MOBILE_COMPOSER_EXPAND_EASING};

  &.stacked {
    grid-template-rows: auto auto;
    grid-template-areas:
      'textarea textarea textarea textarea'
      'attach . mic send';
    row-gap: ${rem(8)};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const MobileTextAreaSlot = styled.div`
  grid-area: textarea;
  min-width: 0;
`

const MobileAttachSlot = styled.div`
  grid-area: attach;
  display: flex;
  align-items: center;
`

const MobileMicSlot = styled.div`
  grid-area: mic;
  display: flex;
  align-items: center;
`

const MobileSendSlot = styled.div`
  grid-area: send;
  display: flex;
  align-items: center;
`

const DisclaimerText = styled(BodyText)`
  &.compact {
    font-size: 10px;
    line-height: 13px;
  }
`

const AttachmentMenuAnchor = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

const FloatingAttachmentMenu = styled.div`
  position: absolute;
  inset-inline-start: 0;
  bottom: calc(100% + ${rem(8)});
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(8)};
  z-index: 3;
`

const FloatingAssistChip = styled(AssistChip)`
  && {
    --chip-stroke-width: 0;
    --chip-stroke-color: transparent;
    background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
    color: ${props => props.theme.palette.neutral.surface.darkContrast};
    box-shadow: ${props => props.theme.shadow.bottom.small};

    svg {
      color: currentColor;
    }

    &:not(:disabled):active {
      background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
      color: ${props => props.theme.palette.neutral.surface.darkContrast};
    }
  }
`

function ChatMessageInput({
  onSubmit,
  disabled = false,
  placeholder,
  value: controlledValue,
  onChange = noop,
  disableSubmit = false,
  isStreaming = false,
  showStopButton = isStreaming,
  onStopStream = noop,
  files,
  onFilesChange,
  selectedTags,
  onAddBookingClick = noop,
  showAddBookingAction = true,
  onRemoveTag = noop,
  inputRef,
  hideDisclaimer = false,
  compactDisclaimer = false,
  disclaimerText: customDisclaimerText,
  autoFocus = true,
  dismissKeyboardOnSubmit = false,
  chatId,
}: ChatMessageInputProps) {
  const intl = useIntl()

  const placeholderEmpty = intl.formatMessage({
    id: 'JreBnLG-TB3nexEa24pF',
    defaultMessage: 'Ask me anything',
    description: 'Placeholder text for empty chat input in TripPlanner',
  })
  const placeholderWithTags = intl.formatMessage({
    id: 'B1-3bpBEjRyHlrbaq_Qr',
    defaultMessage: 'Anything you’d like to add?',
    description: 'Placeholder text for chat input when tags are selected in TripPlanner',
  })
  const defaultDisclaimerText = intl.formatMessage({
    id: '2LcZhOVL1haWs_smYN68',
    defaultMessage: 'AI TripPlanner can make mistakes. Double-check important information.',
    description: 'AI accuracy disclaimer text displayed below the TripPlanner chat input',
  })
  const disclaimerText = customDisclaimerText ?? defaultDisclaimerText
  const uploadPhotoOrFileChipLabel = intl.formatMessage({
    id: 'xFhyB-33Xsfg5y01N-wj',
    defaultMessage: 'Upload a photo or file',
    description: 'Assist chip label shown above TripPlanner chat input to open the existing file upload picker',
  })
  const addBookingChipLabel = intl.formatMessage({
    id: 'N3DmOda7OJ9ekDka-zc2',
    defaultMessage: 'Add a booking',
    description: 'Assist chip label shown above TripPlanner chat input for add booking action placeholder',
  })
  const [internalMessage, setInternalMessage] = useState('')
  const [isAttachmentOptionsVisible, setIsAttachmentOptionsVisible] = useState(false)
  const [areAllTagsVisible, setAreAllTagsVisible] = useState(false)
  const [isComposerFocused, setIsComposerFocused] = useState(false)
  const [isComposerMultiline, setIsComposerMultiline] = useState(false)
  const isMobile = useScreenSizeOnly('mobile')
  const shouldExpandMobileComposer = isComposerFocused || isAttachmentOptionsVisible
  const isComposerStacked = isMobile && (shouldExpandMobileComposer || isComposerMultiline)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const mobileComposerRef = useRef<HTMLDivElement>(null)
  const composerShellRef = useRef<HTMLDivElement>(null)
  const attachmentMenuRef = useRef<HTMLDivElement>(null)
  const latestMessageRef = useRef('')
  const latestDisabledRef = useRef(false)
  const latestDisableSubmitRef = useRef(false)
  const shouldSubmitTranscriptionRef = useRef(false)
  const shouldExpandMobileComposerRef = useRef(false)
  const composerBlurTimeoutRef = useRef<number | undefined>(undefined)

  // Sync external ref with internal ref
  useEffect(() => {
    if (inputRef && textareaRef.current) {
      inputRef.current = textareaRef.current
    }
  })

  const fileInputRef = useRef<HTMLInputElement>(null)
  const message = controlledValue !== undefined ? controlledValue : internalMessage
  const hasMessage = Boolean(message.trim())
  const hasTags = Boolean(selectedTags?.length)
  const selectedTagsKey = selectedTags?.join('\0') ?? ''
  const hasFiles = Boolean(files?.length)
  const hasContent = hasMessage || hasTags || hasFiles
  const placeholderText = placeholder ?? (hasTags ? placeholderWithTags : placeholderEmpty)

  useEffect(() => {
    setAreAllTagsVisible(false)
  }, [selectedTagsKey])

  latestMessageRef.current = message
  latestDisabledRef.current = disabled
  latestDisableSubmitRef.current = disableSubmit
  shouldExpandMobileComposerRef.current = shouldExpandMobileComposer

  const collapseComposer = useCallback(() => {
    setIsComposerFocused(false)
    if (latestMessageRef.current.length === 0) {
      setIsComposerMultiline(false)
    }
  }, [])

  const collapseComposerIfBlurred = useCallback(() => {
    const textarea = textareaRef.current
    if (textarea && document.activeElement === textarea) {
      return
    }
    collapseComposer()
  }, [collapseComposer])

  const resizeTextarea = useCallback(() => {
    if (!textareaRef.current) return
    const textarea = textareaRef.current
    const isEmpty = latestMessageRef.current.length === 0
    const expandForMobileFocus = isMobile && shouldExpandMobileComposerRef.current

    // Restore CSS single-line sizing so the placeholder stays vertically centered
    // with the action buttons after an empty focus → dismiss cycle.
    if (isEmpty && !expandForMobileFocus) {
      textarea.style.minHeight = ''
      textarea.style.height = ''
      textarea.style.overflowY = 'hidden'
      if (isMobile) {
        setIsComposerMultiline(false)
      }
      return
    }

    // Measure content height without the focused min-height so wrap detection stays accurate.
    textarea.style.minHeight = `${COMPOSER_LINE_HEIGHT}px`
    textarea.style.height = 'auto'
    const scrollHeight = textarea.scrollHeight
    const minHeight = expandForMobileFocus
      ? MOBILE_FOCUSED_MIN_HEIGHT
      : COMPOSER_LINE_HEIGHT
    const newHeight = Math.min(Math.max(scrollHeight, minHeight), MAX_HEIGHT)
    textarea.style.minHeight = `${minHeight}px`
    textarea.style.height = `${newHeight}px`
    textarea.style.overflowY = scrollHeight > MAX_HEIGHT ? 'auto' : 'hidden'

    if (isMobile) {
      const hasWrapped = scrollHeight > COMPOSER_LINE_HEIGHT + 1
      setIsComposerMultiline((prev) => {
        if (latestMessageRef.current.length === 0) return false
        return hasWrapped || prev
      })
    }
  }, [isMobile])

  const appendTranscription = useCallback((text: string) => {
    const currentMessage = latestMessageRef.current
    const separator = currentMessage.trim() ? ' ' : ''
    const nextMessage = currentMessage + separator + text

    if (controlledValue !== undefined) {
      onChange(nextMessage)
    } else {
      setInternalMessage(nextMessage)
    }

    requestAnimationFrame(resizeTextarea)
    return nextMessage
  }, [controlledValue, onChange, resizeTextarea])

  const resetMessage = useCallback(() => {
    if (controlledValue !== undefined) {
      onChange('')
    } else {
      setInternalMessage('')
    }
  }, [controlledValue, onChange])

  const settleInputFocusAfterSubmit = useCallback(() => {
    if (dismissKeyboardOnSubmit) {
      textareaRef.current?.blur()
      return
    }

    textareaRef.current?.focus()
  }, [dismissKeyboardOnSubmit])

  const submitMessage = useCallback((nextMessage: string) => {
    if (disabled || disableSubmit) return false

    const processed = normaliseMessage(nextMessage)
    if (!processed && !hasTags && !hasFiles) return false

    onSubmit(processed)
    resetMessage()
    requestAnimationFrame(resizeTextarea)
    settleInputFocusAfterSubmit()
    return true
  }, [disableSubmit, disabled, hasFiles, hasTags, onSubmit, resetMessage, resizeTextarea, settleInputFocusAfterSubmit])

  const submitConfirmedTranscription = useCallback((nextMessage: string) => {
    if (submitMessage(nextMessage)) {
      return true
    }

    // Allow confirmed voice submit in controlled-empty-input edge case.
    const canSubmitAfterControlledUpdate = controlledValue !== undefined &&
      !latestDisabledRef.current &&
      latestDisableSubmitRef.current &&
      !normaliseMessage(controlledValue)

    if (!canSubmitAfterControlledUpdate) {
      return false
    }

    const processed = normaliseMessage(nextMessage)
    if (!processed) {
      return false
    }

    onSubmit(processed)
    resetMessage()
    requestAnimationFrame(resizeTextarea)
    settleInputFocusAfterSubmit()
    return true
  }, [controlledValue, onSubmit, resetMessage, resizeTextarea, settleInputFocusAfterSubmit, submitMessage])

  const handleTranscription = useCallback((text: string) => {
    if (shouldSubmitTranscriptionRef.current) {
      shouldSubmitTranscriptionRef.current = false
      const nextMessage = appendTranscription(text)
      submitConfirmedTranscription(nextMessage)
      return
    }

    appendTranscription(text)
  }, [appendTranscription, submitConfirmedTranscription])

  const handleAfterTranscription = useCallback(() => {
    shouldSubmitTranscriptionRef.current = false
  }, [])

  const {
    isRecording,
    isTranscribing,
    recordingDuration,
    waveformLevels,
    filledBars,
    startRecording,
    cancelRecording,
    submitRecording,
  } = useMicrophoneTranscriber({
    onTranscription: handleTranscription,
    onAfterTranscription: handleAfterTranscription,
  })

  useEffect(() => {
    resizeTextarea()
  }, [message, resizeTextarea])

  useEffect(() => {
    requestAnimationFrame(resizeTextarea)
  }, [isComposerFocused, isAttachmentOptionsVisible, resizeTextarea])

  useEffect(() => {
    if (!isMobile) {
      setIsComposerFocused(false)
      setIsComposerMultiline(false)
    }
  }, [isMobile])

  const handleComposerResize = useCallback(() => {
    requestAnimationFrame(resizeTextarea)
  }, [resizeTextarea])

  useResizeObserver(isMobile ? mobileComposerRef : null, handleComposerResize)

  useEffect(() => {
    const textarea = textareaRef.current
    if (!textarea || !isMobile) {
      return
    }

    // Use native focus/blur listeners. React onFocus is unreliable here because a
    // document capture focusin handler calls stopImmediatePropagation to block
    // content-script interference.
    const handleFocus = () => {
      window.clearTimeout(composerBlurTimeoutRef.current)
      setIsComposerFocused(true)
    }

    const handleBlur = () => {
      // Defer so sibling controls (attach menu, mic) can claim focus / open first.
      window.clearTimeout(composerBlurTimeoutRef.current)
      composerBlurTimeoutRef.current = window.setTimeout(collapseComposerIfBlurred, 0)
    }

    textarea.addEventListener('focus', handleFocus)
    textarea.addEventListener('blur', handleBlur)

    return () => {
      textarea.removeEventListener('focus', handleFocus)
      textarea.removeEventListener('blur', handleBlur)
      window.clearTimeout(composerBlurTimeoutRef.current)
    }
  }, [collapseComposerIfBlurred, isMobile])

  // Mobile web often keeps the textarea focused when tapping non-interactive chrome.
  // Explicitly dismiss so an untouched empty composer collapses again.
  useEffect(() => {
    if (!isMobile || !isComposerFocused) {
      return
    }

    const handlePointerDownOutside = (event: PointerEvent) => {
      if (!(event.target instanceof Node)) {
        return
      }
      if (composerShellRef.current?.contains(event.target)) {
        return
      }

      window.clearTimeout(composerBlurTimeoutRef.current)
      textareaRef.current?.blur()
      collapseComposer()
    }

    document.addEventListener('pointerdown', handlePointerDownOutside, true)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDownOutside, true)
    }
  }, [collapseComposer, isComposerFocused, isMobile])

  useEffect(() => {
    const stopContentScriptsFromFocus = (event: FocusEvent) => {
      if (textareaRef.current && event.target === textareaRef.current) {
        event.stopImmediatePropagation?.()
      }
    }

    document.addEventListener('focusin', stopContentScriptsFromFocus, true)
    return () => {
      document.removeEventListener('focusin', stopContentScriptsFromFocus, true)
    }
  }, [])

  useEffect(() => {
    if (!isAttachmentOptionsVisible) {
      return
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!(event.target instanceof Node)) {
        return
      }

      if (attachmentMenuRef.current?.contains(event.target)) {
        return
      }

      setIsAttachmentOptionsVisible(false)
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsAttachmentOptionsVisible(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('touchstart', handlePointerDown)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('touchstart', handlePointerDown)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isAttachmentOptionsVisible])

  useEffect(() => {
    if (disabled || isTranscribing) {
      setIsAttachmentOptionsVisible(false)
    }
  }, [disabled, isTranscribing])

  const handleSubmit = useCallback(() => {
    submitMessage(message)
  }, [message, submitMessage])

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSubmit()
    }
  }, [handleSubmit])

  const handleChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value
    if (controlledValue !== undefined) {
      onChange(newValue)
    } else {
      setInternalMessage(newValue)
    }
    requestAnimationFrame(resizeTextarea)
  }, [controlledValue, onChange, resizeTextarea])

  const addFiles = useCallback((selectedFiles: Array<File>) => {
    if (!onFilesChange || selectedFiles.length === 0) {
      return
    }

    const { validFiles, invalidFileNames } = partitionChatFiles(selectedFiles)

    if (invalidFileNames.length > 0) {
      const fileNames = invalidFileNames.join(', ')
      showSnackbar(
        intl.formatMessage(
          {
            id: 'MsYqYSByel_JeZ397Cjx',
            defaultMessage: '{count, plural, one {The following file cannot be uploaded} other {The following files cannot be uploaded}}: {fileNames}. Accepted formats: {formats}',
            description: 'Error message when files cannot be uploaded due to unsupported format',
          },
          { count: invalidFileNames.length, fileNames, formats: ACCEPT_EXTENSIONS.replace(/\./g, '').toUpperCase() },
        ),
        'critical',
      )
    }

    if (validFiles.length > 0) {
      onFilesChange([...(files ?? []), ...validFiles])
    }
  }, [intl, files, onFilesChange])

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      return
    }

    addFiles(Array.from(event.target.files))

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    setIsAttachmentOptionsVisible(false)
  }, [addFiles])

  const handlePaste = useCallback((event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!onFilesChange || disabled || isTranscribing) {
      return
    }

    const pastedFiles = getClipboardChatFiles(event.clipboardData)
    if (pastedFiles.length === 0) {
      return
    }

    if (!event.clipboardData.getData('text')) {
      event.preventDefault()
    }
    addFiles(pastedFiles)
    setIsAttachmentOptionsVisible(false)
  }, [addFiles, disabled, isTranscribing, onFilesChange])

  const handleUploadClick = useCallback(() => {
    setIsAttachmentOptionsVisible((prev) => !prev)
  }, [])

  const handleUploadPhotoOrFileClick = useCallback(() => {
    setIsAttachmentOptionsVisible(false)
    fileInputRef.current?.click()
  }, [])

  const handleAddBookingClick = useCallback(() => {
    setIsAttachmentOptionsVisible(false)
    onAddBookingClick()
  }, [onAddBookingClick])

  const handleRemoveTag = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const tag = e.currentTarget.dataset.tag
    if (tag) onRemoveTag(tag)
  }, [onRemoveTag])

  const handleRemoveTagMouseDown = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const tag = e.currentTarget.dataset.tag
    if (tag) onRemoveTag(tag)
  }, [onRemoveTag])

  const handleShowMoreTags = useCallback(() => {
    setAreAllTagsVisible(true)
  }, [])

  const handleRemoveFile = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const index = e.currentTarget.dataset.index
    if (index === undefined || !files || !onFilesChange) return
    const fileIndex = parseInt(index, 10)
    const newFiles = files.filter((_, i) => i !== fileIndex)
    onFilesChange(newFiles)
  }, [files, onFilesChange])

  const handleRecordingCancel = useCallback(() => {
    shouldSubmitTranscriptionRef.current = false
    cancelRecording()
  }, [cancelRecording])

  const handleRecordingSubmit = useCallback(() => {
    trackVoiceInputUsed(chatId)
    shouldSubmitTranscriptionRef.current = true
    submitRecording()
  }, [submitRecording, chatId])

  const microphoneButton = useMemo(() => {
    if (showStopButton) return null

    return (
      <IconButton
        kind="tertiary"
        shape="circle"
        size="small"
        onClick={startRecording}
        disabled={disabled || isTranscribing}
      >
        {isTranscribing ? <LineSpinnerIcon /> : <SolidMicrophoneIcon />}
      </IconButton>
    )
  }, [disabled, isTranscribing, showStopButton, startRecording])

  const ctaButton = useMemo(() => {
    if (showStopButton) {
      return (
        <TextButton
          kind="secondary"
          variant="dark"
          size="medium"
          shape="round"
          onClick={onStopStream}
          endIcon={<LineSquareShapeIcon />}
        >
          {intl.formatMessage({
            id: '_1aP-MlD-tFxuTRBZ91f',
            defaultMessage: 'Stop',
            description: 'Action button label to stop AI response streaming in chat',
          })}
        </TextButton>
      )
    }

    return (
      <MorphingSendButton
        expanded={hasContent}
        iconOnly={isMobile}
        onClick={handleSubmit}
        disabled={disabled || disableSubmit || !hasContent}
      />
    )
  }, [disabled, disableSubmit, handleSubmit, hasContent, intl, isMobile, onStopStream, showStopButton])

  const shouldShowOverlay = isRecording || isTranscribing

  const recordingOverlay = useMemo(() => {
    if (!shouldShowOverlay) {
      return null
    }

    return (
      <RecordingOverlay
        placeholder={placeholderText}
        waveformLevels={waveformLevels}
        filledBars={filledBars}
        durationMs={recordingDuration}
        onCancel={handleRecordingCancel}
        onSubmit={handleRecordingSubmit}
        isTranscribing={isTranscribing}
      />
    )
  }, [
    filledBars,
    handleRecordingCancel,
    handleRecordingSubmit,
    isTranscribing,
    placeholderText,
    recordingDuration,
    shouldShowOverlay,
    waveformLevels,
  ])

  const chipsContent = useMemo(() => {
    if (!hasTags || !selectedTags) return null

    const shouldCollapseTags = isMobile
      && !areAllTagsVisible
      && selectedTags.length > MOBILE_VISIBLE_TAG_LIMIT
    const visibleTags = shouldCollapseTags
      ? selectedTags.slice(0, MOBILE_VISIBLE_TAG_LIMIT)
      : selectedTags
    const hiddenTagsCount = shouldCollapseTags
      ? selectedTags.length - MOBILE_VISIBLE_TAG_LIMIT
      : 0

    return (
      <SelectedTagsGroup direction="horizontal" gap={8} wrap="wrap" fullWidth>
        {visibleTags.map((tag, index) => (
          <ComposerInputChip
            key={`${tag}-${index}`}
            variant="filled"
            data-tag={tag}
            title={tag}
            onClick={handleRemoveTag}
            onMouseDown={handleRemoveTagMouseDown}
            aria-label={intl.formatMessage(
              {
                id: 'Zb2W-kUOdVBBetSY_w-p',
                defaultMessage: 'Remove {tag}',
                description: 'Aria-label for button to remove a selected tag from chat input',
              },
              { tag },
            )}
          >
            {tag}
          </ComposerInputChip>
        ))}
        {hiddenTagsCount > 0 && (
          <TextLink
            size="small"
            onClick={handleShowMoreTags}
            aria-label={intl.formatMessage(
              {
                id: 'w3u8s-OS2brp5XbWgnYq',
                defaultMessage: 'Show {count} more tags',
                description: 'Aria-label for control that expands hidden selected tags in the TripPlanner chat composer',
              },
              { count: hiddenTagsCount },
            )}
          >
            {intl.formatMessage(
              {
                id: 'snr1OuXK2Bayg8-qYIA6',
                defaultMessage: '+{count} more',
                description: 'Link showing count of additional hidden selected tags in the TripPlanner chat composer',
              },
              { count: hiddenTagsCount },
            )}
          </TextLink>
        )}
      </SelectedTagsGroup>
    )
  }, [
    areAllTagsVisible,
    handleRemoveTag,
    handleRemoveTagMouseDown,
    handleShowMoreTags,
    hasTags,
    intl,
    isMobile,
    selectedTags,
  ])

  const filesContent = useMemo(() => {
    if (!files || files.length === 0) return null

    return (
      <Group direction="horizontal" gap={8} wrap="wrap">
        {files.map((file, index) => {
          const isImage = file.type.startsWith('image/')
          const fileExtension = file.name.split('.').pop()?.toUpperCase() || 'FILE'

          return (
            <FilePreviewItem key={`${file.name}-${index}`}>
              <RemoveFileButton
                data-index={index}
                onClick={handleRemoveFile}
                aria-label={intl.formatMessage(
                  {
                    id: 'e8YFBfyWPyJx0RyXqo69',
                    defaultMessage: 'Remove {fileName}',
                    description: 'Aria-label for button to remove an attached file from chat input',
                  },
                  { fileName: file.name },
                )}
              >
                <LineTimesIcon size="XXS" />
              </RemoveFileButton>
              {isImage && <ImageThumbnailPreview file={file} />}
              {!isImage && (
                <FileCard>
                  <FileIconWrapper>
                    <LineFileIcon size="M" />
                  </FileIconWrapper>
                  <FileInfoWrapper>
                    <Group direction="vertical" gap={2}>
                      <BodyText variant="medium" weight="bold" colour="normal-contrast" wrap="truncate" title={file.name}>
                        {file.name}
                      </BodyText>
                      <BodyText variant="small" colour="light-foreground-alt" format="uppercase">
                        {fileExtension}
                      </BodyText>
                    </Group>
                  </FileInfoWrapper>
                </FileCard>
              )}
            </FilePreviewItem>
          )
        })}
      </Group>
    )
  }, [files, handleRemoveFile, intl])

  const attachmentMenu = useMemo(() => {
    if (!onFilesChange) return null

    return (
      <AttachmentMenuAnchor ref={attachmentMenuRef}>
        {isAttachmentOptionsVisible && (
          <FloatingAttachmentMenu>
            <FloatingAssistChip
              variant="filled"
              size="large"
              onClick={handleUploadPhotoOrFileClick}
              startIcon={<LinePaperclipIcon size="S" />}
            >
              {uploadPhotoOrFileChipLabel}
            </FloatingAssistChip>
            {showAddBookingAction && (
              <FloatingAssistChip
                variant="filled"
                size="large"
                onClick={handleAddBookingClick}
                startIcon={<LineCalendarPlusIcon size="S" />}
              >
                {addBookingChipLabel}
              </FloatingAssistChip>
            )}
          </FloatingAttachmentMenu>
        )}
        <IconButton
          kind="tertiary"
          variant="dark"
          shape="circle"
          size="medium"
          horizontalOutdent="start"
          onClick={handleUploadClick}
          disabled={disabled || isTranscribing}
          aria-expanded={isAttachmentOptionsVisible}
          aria-label={intl.formatMessage({
            id: 'V6LhP1J1ebLGBTDknit8',
            defaultMessage: 'Attach files and more',
            description: 'Aria-label for the attach files button in the chat message input',
          })}
        >
          <LinePlusCircleIcon />
        </IconButton>
      </AttachmentMenuAnchor>
    )
  }, [
    addBookingChipLabel,
    disabled,
    handleAddBookingClick,
    handleUploadClick,
    handleUploadPhotoOrFileClick,
    intl,
    isAttachmentOptionsVisible,
    isTranscribing,
    onFilesChange,
    showAddBookingAction,
    uploadPhotoOrFileChipLabel,
  ])

  const hiddenFileInput = onFilesChange ? (
    <HiddenFileInput
      type="file"
      ref={fileInputRef}
      onChange={handleFileSelect}
      multiple
      accept={ACCEPT_EXTENSIONS}
    />
  ) : null

  const messageTextArea = (
    <TextAreaWrapper className={clsx({ stacked: isComposerStacked, hidePlaceholderOnFocus: isMobile })}>
      <TextArea
        inputRef={textareaRef}
        placeholder={placeholderText}
        value={message}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        rows={ROWS}
        resize="none"
        noValidationSpacing
        autoFocus={autoFocus}
      />
    </TextAreaWrapper>
  )

  const mobileComposer = (
    <MobileComposer ref={mobileComposerRef} className={clsx({ stacked: isComposerStacked })}>
      <MobileAttachSlot>{attachmentMenu}</MobileAttachSlot>
      <MobileTextAreaSlot>{messageTextArea}</MobileTextAreaSlot>
      <MobileMicSlot>{microphoneButton}</MobileMicSlot>
      <MobileSendSlot>{ctaButton}</MobileSendSlot>
      {hiddenFileInput}
      {recordingOverlay}
    </MobileComposer>
  )

  const desktopComposer = (
    <Group direction="horizontal" gap={8} verticalAlign="center" fullWidth>
      {attachmentMenu}
      {messageTextArea}
      {microphoneButton}
      {ctaButton}
      {hiddenFileInput}
      {recordingOverlay}
    </Group>
  )

  const composerControls = isMobile ? mobileComposer : desktopComposer

  return (
    <Wrapper>
      <ComposerShellAnchor ref={composerShellRef}>
        <ChatInputShell disabled={disabled} mobileComposer={isMobile}>
          {chipsContent}
          {filesContent}
          {composerControls}
        </ChatInputShell>
      </ComposerShellAnchor>
      {!hideDisclaimer && (
        <DisclaimerText
          variant="small"
          colour="light-foreground-alt"
          className={compactDisclaimer ? 'compact' : undefined}
        >
          {disclaimerText}
        </DisclaimerText>
      )}
    </Wrapper>
  )
}

export default ChatMessageInput
